import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

export interface CategoryBudget {
  id: string;
  user_id: string;
  category: string;
  monthly_limit: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateBudgetInput {
  category: string;
  monthly_limit: number;
  is_active?: boolean;
}

export function useCategoryBudgets() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: budgets = [], isLoading, error } = useQuery({
    queryKey: ['category-budgets', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('category_budgets')
        .select('*')
        .eq('user_id', user.id)
        .order('category', { ascending: true });

      if (error) throw error;
      return data as CategoryBudget[];
    },
    enabled: !!user,
  });

  const createBudget = useMutation({
    mutationFn: async (input: CreateBudgetInput) => {
      if (!user) throw new Error('No user');
      
      const { data, error } = await supabase
        .from('category_budgets')
        .insert({
          user_id: user.id,
          ...input,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-budgets'] });
      toast({ title: 'Presupuesto creado', description: 'El límite de categoría se ha configurado.' });
    },
    onError: (error: Error) => {
      const message = error.message.includes('duplicate') 
        ? 'Ya existe un presupuesto para esta categoría.'
        : 'No se pudo crear el presupuesto.';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  const updateBudget = useMutation({
    mutationFn: async ({ id, ...input }: Partial<CreateBudgetInput> & { id: string }) => {
      const { data, error } = await supabase
        .from('category_budgets')
        .update(input)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-budgets'] });
      toast({ title: 'Presupuesto actualizado' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo actualizar el presupuesto.', variant: 'destructive' });
    },
  });

  const deleteBudget = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('category_budgets')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-budgets'] });
      toast({ title: 'Presupuesto eliminado' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo eliminar el presupuesto.', variant: 'destructive' });
    },
  });

  return {
    budgets,
    isLoading,
    error,
    createBudget,
    updateBudget,
    deleteBudget,
  };
}
